import { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { getIDFromUrl, Url } from '@rossum/api-client';
import { QueueRule } from '@rossum/api-client/schemas';
import { ChoiceDialog } from '@rossum/components/choice-dialog/ChoiceDialog';
import { CopilotDialogV2 } from '@rossum/components/copilot-dialog/CopilotDialogV2';
import { SuggesterIconButton } from '@rossum/components/variable-suggestions/SuggesterIconButton';
import { SuggestVariables } from '@rossum/components/variable-suggestions/SuggestVariables';
import { Stack, Tooltip } from '@rossum/ui/material';
import { useSchema } from '../../../business/schema/useSchema';
import { aiFeaturesEnabledSelector } from '../../../redux/modules/organizationGroup/selectors';
import { getFieldCompletions } from '../../formulas/getFieldCompletions';
import { baseSuggesterSections } from '../../formulas/suggesterSections';
import { useWorkspacesWithQueues } from '../../queues/hooks/useWorkspacesWithQueues';
import { QueueSelectList } from '../../queues/select-queue/QueueSelectList';
import { SelectQueueChip } from '../components/SelectQueueChip';
import { handleInsert } from '../components/SuggestVariables/insertion-helpers';
import {
  narrowRuleSuggestionResultToTemplate,
  useSuggestRule,
} from '../hooks/useSuggestRule';
import { PromptSuggestions } from './prompt-suggestions/PromptSuggestions';

type InitialCopilotProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: (response: Partial<QueueRule>) => void;
  // This is just the default queue that gets prefilled
  defaultQueue?: Url;
  allowQueueChange: boolean;
};

export const InitialCopilot = ({
  open,
  onClose,
  onSuccess,
  defaultQueue,
  allowQueueChange,
}: InitialCopilotProps) => {
  const intl = useIntl();
  const { mutate, isLoading, isError, data } = useSuggestRule();
  const [selectingQueue, setSelectingQueue] = useState(false);
  const aiFeaturesEnabled = useSelector(aiFeaturesEnabledSelector);
  // Without the copilot, only the queue selection is left
  const showQueueSelection = selectingQueue || !aiFeaturesEnabled;

  const [selectedQueues, setSelectedQueues] = useState<number[]>(
    defaultQueue ? [getIDFromUrl(defaultQueue)] : []
  );

  const {
    workspacesWithQueues: queuesByWorkspace,
    queues,
    isLoading: isLoadingWorkspaces,
  } = useWorkspacesWithQueues({
    enableQueries: true,
  });

  const selectedQueueObjects = useMemo(
    () => queues?.filter(queue => selectedQueues.includes(queue.id)) ?? [],
    [queues, selectedQueues]
  );
  const firstQueue = selectedQueueObjects[0];

  const { data: schema } = useSchema(firstQueue?.schema);

  const base = baseSuggesterSections(intl, {
    fields: getFieldCompletions(schema?.content ?? []),
  });

  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  const onSubmit = useCallback(
    (search: string) => {
      if (search && selectedQueueObjects.length > 0 && firstQueue) {
        mutate(
          {
            search,
            queue: firstQueue.url,
          },
          {
            onSuccess: response => {
              const parsedResponse =
                narrowRuleSuggestionResultToTemplate(response);

              const ruleTemplate = parsedResponse[0];

              if (ruleTemplate) {
                onSuccess({
                  ...ruleTemplate,
                  queues: selectedQueueObjects.map(queue => queue.url),
                });
              }
            },
          }
        );
      }
    },
    [firstQueue, mutate, onSuccess, selectedQueueObjects]
  );

  return (
    <>
      <ChoiceDialog
        open={open && showQueueSelection}
        onClose={() => {
          setSelectingQueue(false);
          if (!aiFeaturesEnabled) {
            onClose();
          }
        }}
        onConfirm={() => {
          if (selectedQueueObjects.length > 0) {
            onSuccess({
              queues: selectedQueueObjects.map(queue => queue.url),
            });
          }
        }}
        title={intl.formatMessage({
          id: 'features.rules.selectQueueChip.selectQueue',
        })}
        submitText={intl.formatMessage({
          id: 'components.selectQueueDialog.submit',
        })}
        cancelText={intl.formatMessage({
          id: 'components.selectQueueModal.cancel',
        })}
        isDisabled={selectedQueues.length === 0}
        isLoading={isLoadingWorkspaces}
      >
        {showQueueSelection && (
          <QueueSelectList
            multiSelect
            selectedQueues={selectedQueues}
            onSelect={setSelectedQueues}
            queuesByWorkspace={queuesByWorkspace ?? []}
            isLoading={isLoadingWorkspaces}
          />
        )}
      </ChoiceDialog>
      <CopilotDialogV2
        title={intl.formatMessage({
          id: 'features.queueSettings.rules.initialCopilot.newTitle',
        })}
        suggestingTitle={intl.formatMessage({
          id: 'features.queueSettings.rules.initialCopilot.suggestingTitle',
        })}
        errorText={intl.formatMessage({
          id: 'features.queueSettings.rules.initialCopilot.error',
        })}
        isSuggesting={isLoading}
        onSubmit={onSubmit}
        onClose={onClose}
        open={open && !showQueueSelection}
        submitDisabled={
          selectedQueues.length === 0 || value.length === 0 || !firstQueue
        }
        submitDisabledTooltipTitle={intl.formatMessage({
          id: 'features.rules.copilotSubmitDisabled',
        })}
        isError={isError || data?.results.length === 0}
        placeholder={intl.formatMessage({
          id: `features.queueSettings.rules.initialCopilot.subtitle`,
        })}
        onSkip={() => {
          if (selectedQueueObjects.length > 0) {
            onSuccess({
              queues: selectedQueueObjects.map(queue => queue.url),
            });
          } else {
            setSelectingQueue(true);
          }
        }}
        skipButtonLabel={intl.formatMessage({
          id: 'features.queueSettings.fields.lookupCopilot.skipButtonLabel',
        })}
        confirmText={intl.formatMessage({
          id: `features.queueSettings.rules.initialCopilot.rule.confirm`,
        })}
        renderCopilotToolbar={
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <SelectQueueChip
              selectedQueues={selectedQueues}
              setSelectedQueues={setSelectedQueues}
              disabled={!allowQueueChange}
              dataCy="copilot-dialog-select-queue-chip"
            />
            <SuggestVariables
              sections={[
                base.fields,
                base.annotation,
                base.document,
                base.email,
              ]}
              mode="formula"
              onInsert={handleInsert({
                mode: 'textField',
                inputRef,
                currentValue: value,
                onChange: newValue => setValue(newValue),
              })}
            >
              <Tooltip
                title={intl.formatMessage({
                  id: 'features.rules.copilotVariable',
                })}
                placement="top"
              >
                <span>
                  <SuggesterIconButton
                    disabled={false}
                    dataCy="copilot-dialog-suggester-icon-button"
                  />
                </span>
              </Tooltip>
            </SuggestVariables>
          </Stack>
        }
        renderPromptSuggestions={
          <PromptSuggestions onPromptSelect={setValue} disabled={isLoading} />
        }
        value={value}
        setValue={setValue}
        inputRef={inputRef}
      />
    </>
  );
};
